// Shared availability-status helpers used by both the public "Check
// Availability" page (LandingPage) and the customer "Book Court" flow
// (BookingFlow Step 1), so the two never drift out of visual/logic sync.

// Classify a single {available_courts, total_courts} reading into the same
// 3-tier bucket shown in the Availability Legend.
export function classifyAvailability(availableCourts, totalCourts) {
  if (availableCourts == null || totalCourts == null) return null;
  if (availableCourts <= 0) return 'full';
  const pct = totalCourts > 0 ? (availableCourts / totalCourts) * 100 : 0;
  if (pct <= 40) return 'limited';
  return 'available';
}

export function getAvailabilityText(avail) {
  if (!avail) return 'Loading...';
  if (avail.available_courts === 0) return 'Fully Booked';
  if (avail.available_courts === avail.total_courts) return 'All Available';
  return `${avail.available_courts}/${avail.total_courts} Available`;
}

// Given a per-slot availability map (slot -> {available_courts, total_courts})
// and a starting slot + duration, returns the WORST-CASE availability across
// every consecutive hour the booking would actually occupy. A starting slot
// that looks "Available" on its own but whose 2nd/3rd hour is fully booked
// must never be shown as bookable.
export function getSlotSpanAvailability(availabilityMap, timeSlots, startSlot, duration) {
  const startIndex = timeSlots.indexOf(startSlot);
  if (startIndex === -1) return null;

  const span = [];
  for (let i = 0; i < duration; i++) {
    if (startIndex + i < timeSlots.length) span.push(timeSlots[startIndex + i]);
  }
  const hasEnoughSpan = span.length === duration;

  const entries = span.map((s) => availabilityMap?.[s]).filter(Boolean);
  if (entries.length < span.length) {
    // Not all hours in the span have loaded yet (or don't exist) — unknown.
    return { status: null, available_courts: null, total_courts: null, is_fully_booked: false, has_enough_span: hasEnoughSpan };
  }

  const totalCourts = entries[0].total_courts;
  const minAvailable = Math.min(...entries.map((e) => e.available_courts));
  const status = classifyAvailability(minAvailable, totalCourts);

  return {
    status,
    available_courts: minAvailable,
    total_courts: totalCourts,
    is_fully_booked: minAvailable <= 0,
    has_enough_span: hasEnoughSpan,
  };
}
